/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'
import { Pill } from './Pill.js'
import type { UiRadius, UiSize } from './types.js'
import { pickStyleProps } from './types.js'

export interface TagsInputProps {
  /** Optional list of suggestions — accepts strings or { value, label } objects. */
  data?: (string | { value: string; label: string })[]
  /** Initial tag values. */
  value?: string[]
  /** Placeholder shown when no tags are present. */
  placeholder?: string
  label?: string
  description?: string
  error?: string | boolean
  disabled?: boolean
  /** Maximum number of tags allowed. */
  maxTags?: number
  /** Message shown when no suggestions match. */
  nothingFound?: string
  /** Allow only values from `data` (acts like MultiSelect). Default: false (free input). */
  constrained?: boolean
  /** Separator key(s) that commit a tag. Default: ['Enter', ',']. */
  splitChars?: string[]
  id?: string
  name?: string
  required?: boolean
  withAsterisk?: boolean
  size?: UiSize
  radius?: UiRadius
  class?: string
  [key: string]: unknown
}


function TagsInput({
  data = [],
  value: initialValue = [],
  placeholder = 'Add tags…',
  label,
  description,
  error,
  disabled = false,
  maxTags,
  nothingFound = 'Nothing found',
  constrained = false,
  splitChars = ['Enter', ','],
  id,
  name,
  required = false,
  withAsterisk = false,
  size,
  radius: r,
  class: className = '',
  ...rest
}: TagsInputProps) {
  const [tags, setTags] = createSignal<string[]>(initialValue)
  const [search, setSearch] = createSignal('')
  const [open, setOpen] = createSignal(false)
  const [activeValue, setActiveValue] = createSignal<string | null>(null)

  let inputEl: HTMLInputElement | null = null

  const suggestions = data.map((d) =>
    typeof d === 'string' ? { value: d, label: d } : d,
  )
  const hasSuggestions = suggestions.length > 0

  const filtered = () => {
    if (!hasSuggestions) return []
    const q = search().toLowerCase()
    const existing = new Set(tags())
    return suggestions.filter(
      (s) => !existing.has(s.value) && (!q || s.label.toLowerCase().includes(q)),
    )
  }
  const navigable = () => filtered()
  const atMax = () => maxTags !== undefined && tags().length >= maxTags

  const hasAsterisk = required || withAsterisk
  const hasError = !!error

  const cssVars: Record<string, string> = {}
  if (size !== undefined) {
    cssVars['--dv-input-fz'] = `var(--dv-input-fz-${size})`
    cssVars['--dv-input-size'] = `var(--dv-input-size-${size})`
  }
  if (r !== undefined) {
    const radii: Record<string, string> = {
      xs: 'var(--dv-radius-xs)',
      sm: 'var(--dv-radius-sm)',
      md: 'var(--dv-radius-md)',
      lg: 'var(--dv-radius-lg)',
      xl: 'var(--dv-radius-xl)',
    }
    cssVars['--dv-input-radius'] = radii[r] ?? r
  }
  const { ownRest } = pickStyleProps(rest as Record<string, unknown>)
  const style = Object.keys(cssVars).length
    ? Object.entries(cssVars).map(([k, v]) => `${k}:${v}`).join(';')
    : undefined

  function commitTag(raw: string) {
    const tag = raw.trim()
    if (!tag || atMax() || tags().includes(tag)) return
    if (constrained && !suggestions.find((s) => s.value === tag)) return
    setTags([...tags(), tag])
    setSearch('')
    // Clear the DOM property directly — setAttribute alone doesn't reset a dirty input.
    if (inputEl) inputEl.value = ''
    setActiveValue(null)
  }

  function removeTag(tag: string) {
    setTags(tags().filter((t) => t !== tag))
  }

  function handleInput(e: Event) {
    const v = (e.target as HTMLInputElement).value
    // Fallback for paste or browsers that don't honour keydown preventDefault.
    const splitAt = splitChars.filter((c) => c !== 'Enter').find((c) => v.endsWith(c))
    if (splitAt) {
      commitTag(v.slice(0, -splitAt.length))
      return
    }
    setSearch(v)
    setActiveValue(null)
    if (hasSuggestions && !open()) setOpen(true)
  }

  function handleWrapperClick(e: Event) {
    if (disabled) return
    if ((e.target as HTMLElement).closest('.dv-pill-remove')) return
    ;(e.currentTarget as HTMLElement).querySelector<HTMLInputElement>('.dv-tags-input-search')?.focus()
  }

  function handleBlur() {
    if (!constrained && search().trim()) commitTag(search())
    setTimeout(() => setOpen(false), 150)
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (splitChars.includes(e.key)) {
      e.preventDefault()
      const nav = navigable()
      const active = nav.find((o) => o.value === activeValue())
      if (active) {
        commitTag(active.value)
      } else if (!constrained && search().trim()) {
        // Commit free-form input on any split char (Enter, comma, etc.)
        commitTag(search())
      }
      return
    }

    const nav = navigable()
    const currentIdx = nav.findIndex((o) => o.value === activeValue())

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open() && hasSuggestions) { setOpen(true); return }
      const next = nav[currentIdx + 1]
      if (next) setActiveValue(next.value)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = nav[currentIdx - 1]
      if (prev) setActiveValue(prev.value)
    } else if (e.key === 'Backspace') {
      if (search() === '' && tags().length > 0) {
        removeTag(tags()[tags().length - 1])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div
      class={`dv-input-wrapper dv-tags-input${className ? ` ${className}` : ''}`}
      style={style}
      data-disabled={disabled || undefined}
      data-error={hasError || undefined}
      data-opened={() => open() || undefined}
      {...ownRest}
    >
      {label && (
        <label class='dv-input-label' for={id} data-required={hasAsterisk || undefined}>
          {label}
          {hasAsterisk && (
            <span class='dv-input-asterisk' aria-hidden='true'>
              {' '}
              *
            </span>
          )}
        </label>
      )}
      {description && <p class='dv-input-description'>{description}</p>}
      <div class='dv-tags-input-pills' onClick={handleWrapperClick}>
        {() =>
          tags().map((tag) => (
            <Pill
              size='md'
              withRemoveButton
              onRemove={(e: MouseEvent) => { e.stopPropagation(); removeTag(tag) }}
            >
              {tag}
            </Pill>
          ))
        }
        {() =>
          !atMax() ? (
            <input
              class='dv-tags-input-search'
              id={id}
              ref={(el: Element) => { inputEl = el as HTMLInputElement }}
              value={() => search()}
              placeholder={() => (tags().length === 0 ? placeholder : '')}
              disabled={disabled}
              onInput={handleInput}
              onFocus={() => { if (hasSuggestions && !open()) setOpen(true) }}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
          ) : null
        }
      </div>
      {() =>
        hasSuggestions && open() ? (
          <div class='dv-select-dropdown' role='listbox' data-opened>
            {() => {
              const opts = filtered()
              if (opts.length === 0) return <div class='dv-select-empty'>{nothingFound}</div>
              return opts.map((opt) => (
                <div
                  class='dv-select-option'
                  role='option'
                  aria-selected={false}
                  data-active={opt.value === activeValue() || undefined}
                  onMouseDown={(e: Event) => e.preventDefault()}
                  onClick={() => commitTag(opt.value)}
                >
                  {opt.label}
                </div>
              ))
            }}
          </div>
        ) : null
      }
      {name && tags().map((t) => <input type='hidden' name={`${name}[]`} value={t} />)}
      {hasError && error !== true && <p class='dv-input-error'>{error as string}</p>}
    </div>
  )
}

export default reactive(TagsInput)
